"use client";

import type React from "react";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Textarea,
  VStack,
  Accordion,
  useToast,
  Spinner,
  Divider,
  Input,
} from "@chakra-ui/react";
import { ArrowLeft, Save, Copy, RotateCcw, Send } from "lucide-react";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  smsAgentService,
  type SMSAgent,
} from "../../sms-services/agentService";
import { SMSModelSelect } from "./SMSModelSelect";
import { SMSLanguageSelect } from "./SMSLanguageSelect";
import { SMSAgentFunctions } from "./SMSAgentFunctions";
import { SMSChatSettings } from "./SMSChatSettings";
import { SMSAgentKnowledgeBase } from "./SMSAgentKnowledgeBase";
import { DynamicVariablesButton } from "./DynamicVariablesButton";
import { EditableSMSAgentName } from "./EditableSMSAgentName";

const dotPulse = keyframes`
  0%   { opacity: 0; transform: translateY(2px) scale(0.7); }
  30%  { opacity: 1; transform: translateY(0px) scale(1); }
  60%  { opacity: 1; transform: translateY(0px) scale(1); }
  100% { opacity: 0; transform: translateY(-2px) scale(0.7); }
`;

interface SMSAgentConfigProps {
  agent: SMSAgent;
}

interface Message {
  type: "human" | "ai";
  content: string;
}

function getShortId(id: string) {
  if (!id) return "";
  const len = id.length;
  return len > 5 ? `${id.slice(0, 2)}...${id.slice(-3)}` : id;
}

function generateConversationId(): string {
  const randomStr =
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15);
  return `web-${randomStr}`;
}

const SMSAgentConfig: React.FC<SMSAgentConfigProps> = ({
  agent: initialAgent,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isAdmin } = useAuth();

  const [agent, setAgent] = useState<SMSAgent>(initialAgent);
  const [prompt, setPrompt] = useState(agent.prompt || "");
  const [initialMessage, setInitialMessage] = useState(
    agent.initial_message || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [noteVisible, setNoteVisible] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<
    Record<string, string>
  >({});

  const [isChatMode, setIsChatMode] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const [senderId, setSenderId] = useState<string>(() =>
    generateConversationId()
  );
  const [receiverId, setReceiverId] = useState<string>(() =>
    generateConversationId()
  );

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const scrollbarStyles = {
    "&::-webkit-scrollbar": { width: "8px" },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "gray.300",
      borderRadius: "6px",
      border: "2px solid transparent",
    },
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await smsAgentService.update(agent.agent_id, {
        prompt,
        initial_message: initialMessage,
      });
      toast({
        title: "Agent saved successfully",
        status: "success",
        duration: 3000,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error saving agent",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInitialMessageBlur = async () => {
    try {
      await smsAgentService.update(agent.agent_id, {
        initial_message: initialMessage,
      });
      toast({
        title: "Initial message saved",
        status: "success",
        duration: 2000,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        title: "Error saving initial message",
        description: err.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleDynamicVariablesChange = useCallback(
    (vars: Record<string, string>) => {
      if (Object.keys(vars).length > 0) console.log("Variables updated:", vars);
      setDynamicVariables(vars);
    },
    []
  );

  const handleStartChat = () => {
    setIsChatMode(true);
    // Add the initial AI message if it exists
    if (initialMessage) {
      setMessages([
        {
          type: "ai",
          content: initialMessage,
        },
      ]);
    }
  };

  const handleResetConversation = () => {
    setMessages([]);
    setUserInput("");
    setIsChatMode(false);
    setSenderId(generateConversationId());
    setReceiverId(generateConversationId());
  };

  const handleSendMessage = async () => {
    if (!userInput.trim()) return;

    const userMessage = userInput.trim();
    setUserInput("");

    // Add user message to chat
    const newMessages: Message[] = [
      ...messages,
      {
        type: "human",
        content: userMessage,
      },
    ];
    setMessages(newMessages);
    setIsSendingMessage(true);

    try {
      const formattedDynamicVars: Record<string, string> = {};
      Object.keys(dynamicVariables).forEach((key) => {
        const value = dynamicVariables[key];
        formattedDynamicVars[key] = value.trim() === "" ? `<<${key}>>` : value;
      });

      const response = await fetch(
        `${
          process.env.REACT_APP_SERVER_URL || "http://localhost:8000"
        }/api/sms/ask-agent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            agent_id: agent.agent_id,
            user_message: userMessage,
            dynamic_vars_values: formattedDynamicVars,
            sender_id: senderId,
            receiver_id: receiverId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get response from agent");
      }

      const data = await response.json();
      const agentResponse =
        data.agent_response || data.response || "No response";

      // Add AI response to chat
      setMessages([
        ...newMessages,
        {
          type: "ai",
          content: agentResponse,
        },
      ]);
    } catch (error: any) {
      console.error("[v0] Error sending message:", error);
      toast({
        title: "Error sending message",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const testPanel = useMemo(
    () => (
      <VStack
        w="300px"
        p={4}
        borderLeft="1px solid"
        borderColor="gray.200"
        spacing={4}
      >
        <Flex align="center" justify="space-between" w="full" bg="gray.100">
          <Text px={3} py={1} rounded="md" fontWeight="semibold">
            Test your agent
          </Text>
          <DynamicVariablesButton
            onVariablesChange={handleDynamicVariablesChange}
            prompt={prompt}
            beginMessage={initialMessage}
          />
        </Flex>

        <Divider />

        {!isChatMode ? (
          <Box textAlign="center" py={8}>
            <Box as="span" fontSize="48px">
              <IconButton
                w="full"
                background="transparent"
                _hover={{ background: "transparent" }}
                aria-label="Start Chat"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 50 50"
                    className="chat-bubble-svg"
                  >
                    {/* Bubble outline */}
                    <path
                      d="M 25 4.0703125 C 12.368265 4.0703125 2.0703125 12.921644 2.0703125 24 C 2.0703125 30.432481 5.5907163 36.030749 11.003906 39.6875 C 10.995106 39.903125 11.010706 40.250912 10.728516 41.294922 C 10.378462 42.590119 9.6725023 44.413033 8.2382812 46.46875 L 7.21875 47.929688 L 9 47.929688 C 15.17102 47.929688 18.741544 43.907595 19.294922 43.261719 C 21.134317 43.693171 23.024914 43.929686 25 43.929688 C 37.631735 43.929688 47.929688 35.078356 47.929688 24 C 47.929688 12.921644 37.631735 4.0703125 25 4.0703125 z M 25 5.9296875 C 36.768265 5.9296875 46.070312 14.078356 46.070312 24 C 46.070312 33.921644 36.768265 42.070312 25 42.070312 C 22.959016 42.070312 21.040073 41.87506 19.236328 41.400391 L 18.654297 41.248047 L 18.277344 41.714844 C 18.277344 41.714844 15.390792 44.972581 10.78125 45.757812 C 11.617979 44.248964 12.233683 42.844161 12.521484 41.779297 C 12.921431 40.299494 12.929687 39.300781 12.929688 39.300781 L 12.929688 38.789062 L 12.5 38.515625 C 7.2214165 35.156526 3.9296875 29.955741 3.9296875 24 C 3.9296875 14.078356 13.231735 5.9296875 25 5.9296875 z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    {/* Dots (always visible, animate on hover) */}
                    <circle
                      className="dot dot-1"
                      cx="18"
                      cy="24"
                      r="2.4"
                      fill="currentColor"
                    />
                    <circle
                      className="dot dot-2"
                      cx="25"
                      cy="24"
                      r="2.4"
                      fill="currentColor"
                    />
                    <circle
                      className="dot dot-3"
                      cx="32"
                      cy="24"
                      r="2.4"
                      fill="currentColor"
                    />
                  </svg>
                }
                sx={{
                  color: "blue.400",

                  "& .chat-bubble-svg": {
                    transition: "filter 0.2s ease, transform 0.2s ease",
                  },

                  "&:hover .chat-bubble-svg": {
                    filter: "drop-shadow(0 0 10px rgba(59,130,246,0.9))",
                    transform: "scale(1.04)",
                  },

                  /* ✅ Base state: dots ALWAYS visible, NO animation */
                  "& .dot": {
                    opacity: 1,
                    animation: "none",
                    transformOrigin: "center",
                  },

                  /* ✅ Hover state: apply the pulsing animation */
                  "&:hover .dot": {
                    animation: `${dotPulse} 1.2s infinite`,
                  },

                  /* stagger left → right */
                  "&:hover .dot-1": { animationDelay: "0ms" },
                  "&:hover .dot-2": { animationDelay: "200ms" },
                  "&:hover .dot-3": { animationDelay: "400ms" },
                }}
              />
            </Box>

            <Button
              mt={4}
              w="full"
              colorScheme="blue"
              bg="blue.500"
              _hover={{ bg: "blue.600" }}
              variant="solid"
              onClick={handleStartChat}
            >
              Start Chat
            </Button>
          </Box>
        ) : (
          <VStack w="full" h="calc(100vh - 200px)" spacing={0}>
            {/* Relative container so the reset button can float over the messages */}
            <Box
              position="relative"
              w="full"
              flex="1"
              display="flex"
              flexDirection="column"
              minH="0"
            >
              {/* Floating reset button */}
              <IconButton
                aria-label="Reset conversation"
                icon={<RotateCcw size={16} />}
                size="sm"
                variant="ghost"
                onClick={handleResetConversation}
                title="Reset conversation"
                position="absolute"
                top="2"
                right="2"
                zIndex={2}
                bg="whiteAlpha.800"
                _hover={{ bg: "whiteAlpha.900" }}
                boxShadow="sm"
                borderRadius="full"
              />

              {/* Scrollable messages */}
              <VStack
                flex={1}
                w="full"
                overflowY="auto"
                spacing={3}
                align="stretch"
                css={scrollbarStyles}
                p={2}
                bg="gray.50"
                borderRadius="md"
                minH="0"
              >
                {messages.map((msg, idx) => (
                  <Box
                    key={idx}
                    alignSelf={msg.type === "human" ? "flex-end" : "flex-start"}
                    maxW="85%"
                  >
                    <Box
                      bg={msg.type === "human" ? "blue.500" : "white"}
                      color={msg.type === "human" ? "white" : "gray.800"}
                      px={3}
                      py={2}
                      borderRadius="lg"
                      fontSize="sm"
                      boxShadow="sm"
                      border={msg.type === "ai" ? "1px solid" : "none"}
                      borderColor="gray.200"
                    >
                      {msg.content}
                    </Box>
                  </Box>
                ))}

                {isSendingMessage && (
                  <Box alignSelf="flex-start" maxW="85%">
                    <Box
                      bg="white"
                      color="gray.800"
                      px={3}
                      py={2}
                      borderRadius="lg"
                      fontSize="sm"
                      boxShadow="sm"
                      border="1px solid"
                      borderColor="gray.200"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <style>
                        {`
                        @keyframes typing-dot {
                          0%, 60%, 100% { opacity: 0.3; }
                          30% { opacity: 1; }
                        }
                      `}
                      </style>
                      <Box
                        as="span"
                        w="6px"
                        h="6px"
                        bg="gray.500"
                        borderRadius="full"
                        display="inline-block"
                        animation="typing-dot 1.4s infinite"
                        style={{ animationDelay: "0s" }}
                      />
                      <Box
                        as="span"
                        w="6px"
                        h="6px"
                        bg="gray.500"
                        borderRadius="full"
                        display="inline-block"
                        animation="typing-dot 1.4s infinite"
                        style={{ animationDelay: "0.2s" }}
                      />
                      <Box
                        as="span"
                        w="6px"
                        h="6px"
                        bg="gray.500"
                        borderRadius="full"
                        display="inline-block"
                        animation="typing-dot 1.4s infinite"
                        style={{ animationDelay: "0.4s" }}
                      />
                    </Box>
                  </Box>
                )}

                <div ref={chatEndRef} />
              </VStack>
            </Box>

            {/* Composer */}
            <Flex w="full" mt={3} gap={2}>
              <Input
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                size="sm"
                disabled={isSendingMessage}
                bg="white"
              />
              <IconButton
                aria-label="Send message"
                icon={<Send size={16} />}
                size="sm"
                colorScheme="blue"
                onClick={handleSendMessage}
                isLoading={isSendingMessage}
                disabled={!userInput.trim() || isSendingMessage}
              />
            </Flex>
          </VStack>
        )}
      </VStack>
    ),
    [
      handleDynamicVariablesChange,
      prompt,
      initialMessage,
      isChatMode,
      messages,
      userInput,
      isSendingMessage,
      scrollbarStyles,
    ]
  );

  const mainContent = useMemo(
    () => (
      <Flex h="100vh" bg="white" color="gray.800" direction="column">
        {/* Header */}
        <Flex
          px={4}
          py={3}
          borderBottom="1px solid"
          borderColor="gray.200"
          align="center"
        >
          <IconButton
            aria-label="Back"
            icon={<ArrowLeft />}
            variant="ghost"
            color="gray.800"
            _hover={{ bg: "gray.100" }}
            onClick={() => navigate("/sms-agents")}
          />
          <Box ml={4}>
            <EditableSMSAgentName
              agentId={agent.agent_id}
              initialName={agent.name || ""}
            />
            <Text color="gray.500" fontSize="sm">
              SMS &bull; Agent ID: {getShortId(agent.agent_id)}{" "}
              <IconButton
                aria-label="Copy Agent ID"
                icon={<Copy size={12} />}
                size="xs"
                variant="ghost"
                onClick={() => copyToClipboard(agent.agent_id)}
                color="gray.500"
              />
            </Text>
          </Box>
        </Flex>

        {/* Toolbar */}
        <Flex
          px={4}
          py={2}
          borderBottom="1px solid"
          borderColor="gray.200"
          gap={2}
        >
          <SMSModelSelect
            agentId={agent.agent_id}
            initialModel={agent.llm || "gpt-4o"}
            initialTemperature={agent.llm_temperature}
            onModelUpdate={(newModel) => setAgent({ ...agent, llm: newModel })}
          />
          <SMSLanguageSelect
            agentId={agent.agent_id}
            initialLanguage={agent.language || "en-US"}
            onLanguageUpdate={(newLang) =>
              setAgent({ ...agent, language: newLang })
            }
          />
        </Flex>

        {/* Body */}
        <Flex flex={1} overflow="hidden" alignItems="stretch">
          {/* Prompt editor */}
          <Box
            flex={1}
            p={4}
            borderRight="1px solid"
            borderColor="gray.200"
            maxW="calc(100% - 600px)"
            minW="500px"
          >
            <VStack
              align="stretch"
              h="full"
              spacing={4}
              overflowY="auto"
              css={scrollbarStyles}
            >
              <Box position="relative" flex={0} minH="400px">
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                  }}
                  resize="none"
                  fontSize="sm"
                  placeholder="Type in a universal prompt for your agent, such as its role, conversational style, objective, etc."
                  pr="100px"
                  height="100%"
                  onMouseEnter={() => setNoteVisible(true)}
                  onMouseLeave={() => setNoteVisible(false)}
                  isReadOnly={!isAdmin}
                />
                {!isAdmin && (
                  <Text fontSize="xs" color="orange.400" mt={1}>Prompt editing is restricted to administrators.</Text>
                )}
                <Button
                  leftIcon={<Save size={16} />}
                  onClick={handleSave}
                  isLoading={isSaving}
                  position="absolute"
                  bottom="8px"
                  right="8px"
                  size="sm"
                  colorScheme="blue"
                  bg="blue.500"
                  _hover={{ bg: "blue.600" }}
                  zIndex={2}
                >
                  Save
                </Button>
                {noteVisible && (
                  <Text
                    position="absolute"
                    bottom="8px"
                    left="8px"
                    fontSize="xs"
                    color="gray.500"
                  >
                    Use `{"{{_}}"}` to add variables.
                  </Text>
                )}
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  Initial Message
                </Text>
                <Input
                  value={initialMessage}
                  onChange={(e) => setInitialMessage(e.target.value)}
                  onBlur={handleInitialMessageBlur}
                  placeholder="Enter the initial message to send when chat starts"
                  size="md"
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                  }}
                />
              </Box>
            </VStack>
          </Box>

          {/* Right rail */}
          <VStack
            w="300px"
            spacing={0}
            overflow="auto"
            p={4}
            borderRight="1px solid"
            borderColor="gray.200"
            css={scrollbarStyles}
          >
            <Accordion allowMultiple w="full">
              {isAdmin && (
                <SMSAgentFunctions
                  agentId={agent.agent_id}
                  initialFunctions={
                    agent.tools?.map((tool) => {
                      if (typeof tool === "string") {
                        try {
                          let parsed = JSON.parse(tool);
                          if (typeof parsed === "string") {
                            parsed = JSON.parse(parsed);
                          }
                          if (parsed && typeof parsed === "object") {
                            if (
                              typeof parsed.type === "string" &&
                              parsed.type.startsWith("{")
                            ) {
                              try {
                                parsed = JSON.parse(parsed.type);
                              } catch {
                                // If parsing type fails, keep the original parsed object
                              }
                            }
                          }
                          return parsed;
                        } catch (e) {
                          console.error("[v0] Error parsing tool:", tool, e);
                          return { type: tool, name: tool };
                        }
                      }
                      return tool;
                    }) || []
                  }
                />
              )}

              <SMSAgentKnowledgeBase agentId={agent.agent_id} />

              <SMSChatSettings
                agentId={agent.agent_id}
                initialMaxChatLength={agent.max_chat_length || 50}
                initialTone={agent.tone || "professional"}
              />
            </Accordion>
          </VStack>

          {/* Test Panel */}
          {testPanel}
        </Flex>
      </Flex>
    ),
    [agent, prompt, initialMessage, noteVisible, navigate, testPanel, isSaving]
  );

  return isLoading ? (
    <Flex h="100vh" justify="center" align="center" bg="white">
      <Spinner size="xl" color="blue.500" />
    </Flex>
  ) : (
    mainContent
  );
};

export default SMSAgentConfig;
